/* eslint-disable max-lines */
import { CSSProperties, ReactNode } from "react";
import { Duration } from "date-fns";
import {
  EqlRequestDc,
  GeometryType,
  LayerReferenceConfigurationDc,
  PagedFeaturesListDc,
  PositionDc,
  AttributeDefinitionDc,
  FeatureDc,
} from "@evergis/api";
import { IOption, ThemeName } from "@evergis/ui";
import { BarChartMarker, PieChartData } from "@evergis/charts";
import { Projection } from "@evergis/sgis/es/Crs";
import { SrId } from "@evergis/react";

import { FeatureAttribute } from "./components/Attributes/FeatureAttributeField";
import { LayerInfo } from "../../interfaces/resourceInfo";
import { EditGeometryType } from "../../evergis/core/attributes/geometryTypes";
import { Positions } from "../../ducks/geometryEditor/geometryEditor";
import { FeatureDcExtended } from "../../ducks/selectFeatures/types";
import { Layer } from "../DateFilter/reducers";
import { NoFeatureService } from "./components/NoFeatureCard/types";

export enum FeatureCardMode {
  Create = "Create",
  Edit = "Edit",
}

export type ConfigRelatedAttribute = {
  layerName: string;
  attributeName: string;
  attributeTitle?: string;
  condition?: string;
};

export type ConfigRelatedDataSource = {
  dataSourceName: string;
  attributeName: string;
  attributeTitle: string;
  attributeColor?: string;
  chartAxis?: string;
  axisColor?: string;
  hideAxis?: boolean;
  filterName?: string;
};

export interface BaseMapSettings {
  opacity?: number;
  showBuildings?: boolean;
}

export type ConfigOptions = {
  innerTemplateName?: ContainerTemplate;
  innerTemplateStyle?: CSSProperties;
  position?: PositionDc;
  themeName?: ThemeName;
  relatedDataSources?: ConfigRelatedDataSource[];
  relatedAttributes?: ConfigRelatedAttribute[];
  chartType?: "bar" | "line" | "pie" | "stack";
  layerNames?: string[];
  noFeatureServices?: NoFeatureService[];
  layerName?: string;
  geometryType?: GeometryType | EditGeometryType;
  srid?: SrId;
  title?: string;
  label?: string;
  withPadding?: boolean;
  withDivider?: boolean;
  tagView?: boolean;
  separator?: string;
  lineBreak?: string;
  image?: string;
  bgColor?: string;
  noBg?: boolean;
  center?: boolean;
  fontColor?: string;
  fontSize?: string;
  placeholder?: string;
  step?: number;
  limit?: number;
  radius?: number;
  cornerRadius?: number;
  padding?: number;
  trim?: boolean;
  twoColumns?: boolean;
  inlineUnits?: boolean;
  noUnits?: boolean;
  noEmptyOption?: boolean;
  chartId?: string;
  showTotal?: boolean;
  totalWord?: string;
  totalAttribute?: string;
  hideTitle?: boolean;
  width?: number;
  height?: number;
  barWidth?: number;
  barHeight?: number;
  markers?: BarChartMarker[];
  drawMinMax?: boolean;
  attributes?: string[];
  colors?: string[];
  defaultColor?: string;
  primaryColor?: string;
  maxLength?: number;
  maxValue?: number | Date;
  minValue?: number | Date;
  withTime?: boolean;
  multiSelect?: boolean;
  variants?: IOption[];
  shownItems?: number;
  otherItems?: number;
  angle?: number;
  resolution?: number;
  orderByValue?: boolean;
  orderByTitle?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  wrap?: boolean;
  icon?: string;
  iconUrl?: string;
  bigIcon?: boolean;
  onlyIcon?: boolean;
  hideEmpty?: boolean;
  innerValue?: boolean;
  groupTooltip?: boolean;
  tabId?: string;
  column?: boolean;
  relatedDataSource?: string;
  filterName?: string;
  searchFilterName?: string;
  colorAttribute?: string;
  pitch?: number;
  bearing?: number;
  projection?: Projection["name"];
  baseMapName?: string;
  baseMapSettings?: Record<string, BaseMapSettings>;
  expandedLayers?: boolean;
};

export type ConfigDataSource = {
  name: string;
  condition: string;
  layerName?: string;
  limit?: number;
  offset?: number;
  query?: string;
  resourceId?: string;
  url?: string;
  type?: string;
  parameters?: EqlRequestDc["parameters"];
};

export type EqlDataSource = {
  items: FeatureDc[];
  totalCount?: number;
  attributeDefinition?: Record<string, AttributeDefinitionDc>;
};

export type ConfigFilterValueType = "single" | "range" | "array";

export type ConfigFilter = {
  name: string;
  relatedDataSource?: string;
  defaultValue?: SelectedFilter["value"];
  attributeAlias?: string;
  attributeValue?: string;
  attributeMin?: string;
  attributeMax?: string;
  valueType?: ConfigFilterValueType;
  resetFilters?: string[];
};

export type FilterItem = {
  name: string;
  value: string | number;
  color: string;
  min: string | number;
  max: string | number;
};

export type SelectedFilter = {
  value: string | number | string[] | number[] | Date | Date[];
  min?: string | number | Date;
  max?: string | number | Date;
};

export type SelectedFilters = Record<string, SelectedFilter>;

export type ConfigLayer = {
  name: string;
  condition?: string;
  opacity?: number;
  selectable?: boolean;
  filterZoomTo?: boolean;
  isVisible?: boolean;
};

export type ConfigContainer = {
  currentConfig?: ConfigContainer;
  templateName?: ContainerTemplate;
  children: ConfigContainerChild[];
  style?: CSSProperties;
  options?: ConfigOptions;
  dataSources?: ConfigDataSource[];
  filters?: ConfigFilter[];
  dateFilter?: { layers: Layer[]; isVisible: boolean; rangeDate: string[]; durationInterval: Duration };
  layers?: ConfigLayer[];
  header?: {
    templateName?: HeaderTemplate;
    children?: ConfigContainerChild[];
    options?: Pick<ConfigOptions, "withPadding">;
  };
};

export type FilterType = "checkbox" | "rangeNumber" | "rangeDate" | "text" | "dropdown" | "barChart";

export type ConfigContainerChild = Partial<ConfigContainer> & {
  id?: string;
  type?: string;
  attributeName?: string | string[];
  attributeIcon?: string;
  attributeUnits?: string;
  defaultValue?: string | number;
  value?: string | number;
};

export enum ContainerTemplate {
  Pages = "Pages",
  Pagination = "Pagination",
  ContainersGroup = "ContainersGroup",
  DefaultAttributes = "DefaultAttributes",
  Tabs = "Tabs",
  Attribute = "Attribute",
  OneColumn = "OneColumn",
  TwoColumn = "TwoColumn",
  RoundedBackground = "RoundedBackground",
  Progress = "Progress",
  Image = "Image",
  Icon = "Icon",
  TwoColumnsInner = "TwoColumnsInner",
  Chart = "Chart",
  DataSource = "DataSource",
  DataSourceProgress = "DataSourceProgress",
  Filters = "Filters",
  Title = "Title",
  Slideshow = "Slideshow",
  Camera = "Camera",
  Layers = "Layers",
  YandexMap = "YandexMap",
  AddFeature = "AddFeature",
  Divider = "Divider",
}

export enum HeaderTemplate {
  Default = "Default",
  Slideshow = "Slideshow",
  Gradient = "Gradient",
  Icon = "Icon",
}

export type RenderElementFuncProps = Omit<ContainerProps, "renderElement"> & {
  expandedContainers?: Record<string, boolean>;
  page?: number;
  selectedTabId?: string;
  setSelectedTabId?: (id: string) => void;
};

export type RenderElementProps = {
  id?: string;
  index?: number;
};

export enum WidgetType {
  Dashboard = "Dashboard",
  FeatureCard = "FeatureCard",
}

export type ContainerProps = {
  config?: ConfigContainer;
  elementConfig?: ConfigContainerChild;
  attributes?: FeatureAttribute[];
  layerInfo?: LayerInfo;
  maxValue?: number;
  isRow?: boolean;
  isVisible?: boolean;
  type?: WidgetType;
  index?: number;
  renderElement?: <IsWrapped extends boolean>(
    props: RenderElementProps & {
      wrap?: IsWrapped;
    },
  ) => IsWrapped extends true ? ReactNode : string;
};

export type FeatureCardChartProps = Pick<
  ContainerProps,
  "config" | "elementConfig" | "attributes" | "type" | "renderElement"
> & {
  element: ConfigContainerChild;
};

export type FeatureCardProps = {
  attributes: FeatureAttribute[];
  itemsCount: number;
  featureLayer: string;
  layerInfo: LayerInfo;
  featureName: string;
  feature: FeatureDcExtended;
  coordinates: Positions;
  handBookReference: LayerReferenceConfigurationDc;
  geometryType: GeometryType | EditGeometryType;
  isRaster: boolean;
  isEdit: boolean;
  isFeatureEditable: boolean;
  hasCopyRights: boolean;
  editOnly: boolean;
  selectedTabId: string;
  expandedContainers: Record<string, boolean>;
  filters: SelectedFilters;
  closeCard: VoidFunction;
  zoomToFeature: (feature: FeatureDcExtended) => void;
  changeFilters: (filters: SelectedFilters, resetFilters?: string[]) => void;
  expandContainer: (id: string, expanded?: boolean) => void;
  toggleEditMode: VoidFunction;
  saveAttributes: VoidFunction;
  setSelectedTabId: (id: string) => void;
  setHandBookReference: (reference: LayerReferenceConfigurationDc) => void;
  nextPage: (total: number) => void;
  prevPage: (total: number) => void;
  changePage: (page: number) => void;
};

export type PieChartDisplayedData = Array<Omit<PieChartData, "value"> & { value: string | number }>;

export type DataSourcePromise = PromiseSettledResult<EqlDataSource | PagedFeaturesListDc>;
